import React, { useState, useEffect } from 'react';
import { Loader2, RefreshCw, Zap, ExternalLink } from 'lucide-react';

/**
 * CompanyEnrichmentPanel — gedeeld "Bedrijfsinfo" tabblad (AI-verrijking)
 * voor de hele pipeline: lead, prospect, onboarding en client. Leest de
 * verrijkte bedrijfsdata uit de lead-rij en kan opnieuw verrijken via
 * POST /api/v1/ai/enrich-lead/{id}.
 */

const getToken = () => sessionStorage.getItem('auth_token');

export default function CompanyEnrichmentPanel({ leadId, className = '' }) {
  const [enrichment, setEnrichment] = useState(null);
  const [enriching, setEnriching] = useState(false);

  const fetchEnrichment = async () => {
    setEnriching(true);
    try {
      const resp = await fetch(`/api/v1/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      // Parse company_description — may be JSON blob from AI enrichment
      let parsed = {};
      const raw = data.company_description || '';
      if (raw) {
        let cleaned = raw.trim().replace(/^```[a-z]*\n?/i, '').replace(/```$/g, '').trim();
        try { parsed = JSON.parse(cleaned); } catch { parsed = { description: raw }; }
      }
      setEnrichment({
        description:          parsed.description || '',
        industry:             parsed.industry || data.company_industry || '',
        company_size:         parsed.company_size || data.company_size || '',
        founding_year:        parsed.founding_year || null,
        headquarters:         parsed.headquarters || '',
        annual_revenue_estimate: parsed.annual_revenue_estimate || parsed.estimated_revenue || '',
        kvk:                  parsed.kvk_number || data.kvk_number || '',
        international_trade:  parsed.international_trade ?? null,
        trade_countries:      parsed.trade_countries || [],
        products_services:    parsed.products_services || [],
        fx_relevance:         parsed.fx_relevance || '',
        pain_points:          parsed.pain_points || [],
        business_segments:    parsed.business_segments || [],
        fit_score:            parsed.fit_score ?? null,
        fit_reason:           parsed.fit_reason || '',
        taper_fit_products:   parsed.taper_fit_products || [],
        linkedin:             parsed.linkedin_url || data.linkedin_url || '',
        website:              parsed.website || data.company_website || '',
        country:              data.company_country || '',
        phone:                parsed.phone || '',
        enriched_at:          parsed.enriched_at || data.updated_at,
      });
    } catch {
      setEnrichment({});
    }
    setEnriching(false);
  };

  useEffect(() => {
    if (leadId) fetchEnrichment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleReEnrich = async () => {
    setEnriching(true);
    try {
      const resp = await fetch(`/api/v1/ai/enrich-lead/${leadId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setEnrichment(null);
      await fetchEnrichment();
    } catch (e) {
      console.error('Enrichment failed:', e);
      setEnriching(false);
    }
  };

  return (
    <div className={`space-y-5 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-[#566079] uppercase tracking-wider">Bedrijfsinformatie</h3>
        <button
          onClick={handleReEnrich}
          disabled={enriching}
          className="flex items-center gap-2 text-[#3d61a4] hover:text-[#0a2d6b] font-medium text-sm disabled:opacity-50"
        >
          {enriching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Verrijk met AI
        </button>
      </div>

      {enriching && !enrichment ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 size={28} className="animate-spin text-[#3d61a4]" />
          <p className="text-sm text-[#566079]">Bedrijfsdata ophalen via website & AI...</p>
        </div>
      ) : !enrichment || (!enrichment.description && enrichment.fit_score == null) ? (
        <div className="text-center py-10 space-y-3">
          <div className="w-12 h-12 rounded-full bg-[#eef2fa] flex items-center justify-center mx-auto">
            <Zap size={22} className="text-[#3d61a4]" />
          </div>
          <p className="text-sm text-[#566079]">Nog geen bedrijfsanalyse beschikbaar.</p>
          <p className="text-xs text-[#a4abbe]">Klik op "Verrijk met AI" om een volledig bedrijfsprofiel op te halen.</p>
        </div>
      ) : (
        <>
          {/* Taper Fit Score */}
          {enrichment.fit_score != null && (
            <div className="rounded-xl p-4 flex items-start gap-4" style={{
              background: enrichment.fit_score >= 7
                ? 'linear-gradient(135deg, #011745 0%, #0a2d6b 100%)'
                : enrichment.fit_score >= 4
                ? 'linear-gradient(135deg, #78350f 0%, #b45309 100%)'
                : 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 100%)'
            }}>
              <div className="w-14 h-14 rounded-xl flex flex-col items-center justify-center bg-white/15 flex-shrink-0">
                <span className="text-2xl font-bold text-white leading-none">{enrichment.fit_score}</span>
                <span className="text-[9px] text-white/70 font-medium">/10</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-0.5">Taper Fit Score</p>
                <p className="text-sm text-white font-medium leading-snug">{enrichment.fit_reason || '—'}</p>
                {enrichment.taper_fit_products?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {enrichment.taper_fit_products.map((p, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/20 text-white font-medium">{p}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {enrichment.description && (
            <div className="bg-[#f7f8fc] rounded-xl p-4 border border-[#e8eaf2]">
              <p className="text-xs font-semibold uppercase text-[#7b859e] mb-2">Over het bedrijf</p>
              <p className="text-sm text-[#566079] leading-relaxed">{enrichment.description}</p>
            </div>
          )}

          {/* Key stats grid */}
          <div className="grid grid-cols-2 gap-2">
            {[
              ['Industrie', enrichment.industry],
              ['Bedrijfsgrootte', enrichment.company_size],
              ['Opgericht', enrichment.founding_year],
              ['Hoofdkantoor', enrichment.headquarters],
              ['Geschatte omzet', enrichment.annual_revenue_estimate],
              ['KVK', enrichment.kvk],
              ['Land', enrichment.country],
              ['Telefoon', enrichment.phone],
            ].filter(([, v]) => v).map(([label, val]) => (
              <div key={label} className="p-3 rounded-lg bg-[#f7f8fc] border border-[#e8eaf2]">
                <p className="text-[10px] font-semibold uppercase text-[#a4abbe] mb-0.5">{label}</p>
                <p className="text-sm font-medium text-[#011745] truncate">{val}</p>
              </div>
            ))}
          </div>

          {/* Links */}
          <div className="space-y-2">
            {enrichment.website && (
              <a href={enrichment.website.startsWith('http') ? enrichment.website : `https://${enrichment.website}`}
                target="_blank" rel="noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-[#f7f8fc] border border-[#e8eaf2] hover:border-[#3d61a4] transition-colors group">
                <span className="text-xs text-[#7b859e]">Website</span>
                <span className="text-sm text-[#3d61a4] font-medium flex items-center gap-1 group-hover:underline">
                  {enrichment.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  <ExternalLink size={11} />
                </span>
              </a>
            )}
            {enrichment.linkedin && (
              <a href={enrichment.linkedin.startsWith('http') ? enrichment.linkedin : `https://${enrichment.linkedin}`}
                target="_blank" rel="noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-[#f7f8fc] border border-[#e8eaf2] hover:border-[#3d61a4] transition-colors group">
                <span className="text-xs text-[#7b859e]">LinkedIn</span>
                <span className="text-sm text-[#3d61a4] font-medium flex items-center gap-1 group-hover:underline">
                  LinkedIn profiel <ExternalLink size={11} />
                </span>
              </a>
            )}
          </div>

          {/* International trade + countries */}
          {enrichment.international_trade != null && (
            <div className="p-4 rounded-xl border border-[#e8eaf2] bg-[#f7f8fc]">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${enrichment.international_trade ? 'bg-green-500' : 'bg-gray-400'}`} />
                <p className="text-xs font-semibold text-[#566079]">
                  {enrichment.international_trade ? 'Internationaal actief' : 'Voornamelijk lokaal'}
                </p>
              </div>
              {enrichment.trade_countries?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {enrichment.trade_countries.map((c, i) => (
                    <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-[#e8eaf2] text-[#566079]">{c}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* FX Relevance */}
          {enrichment.fx_relevance && (
            <div className="p-4 rounded-xl bg-[#eef2fa] border border-[#c7d4ef]">
              <p className="text-[10px] font-semibold uppercase text-[#3d61a4] mb-1">FX / Betalingsbehoefte</p>
              <p className="text-sm text-[#566079]">{enrichment.fx_relevance}</p>
            </div>
          )}

          {/* Pain Points */}
          {enrichment.pain_points?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-[#7b859e] mb-2">Financiële pijnpunten</p>
              <div className="space-y-1.5">
                {enrichment.pain_points.map((pp, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-[#566079]">
                    <span className="text-amber-500 mt-0.5 flex-shrink-0">⚡</span>
                    <span>{pp}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Products / Services */}
          {enrichment.products_services?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-[#7b859e] mb-2">Producten & Diensten</p>
              <div className="flex flex-wrap gap-1.5">
                {enrichment.products_services.map((s, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-[#f7f8fc] border border-[#e8eaf2] text-[#566079]">{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Business Segments */}
          {enrichment.business_segments?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-[#7b859e] mb-2">Zakelijke segmenten</p>
              <div className="flex flex-wrap gap-1.5">
                {enrichment.business_segments.map((seg, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-[#eef2fa] text-[#3d61a4] font-medium">{seg}</span>
                ))}
              </div>
            </div>
          )}

          {/* Timestamp */}
          {enrichment.enriched_at && (
            <p className="text-[10px] text-[#a4abbe] text-right">
              Verrijkt op {new Date(enrichment.enriched_at).toLocaleString('nl-NL')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
